import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Database, Cloud, Webhook, Key, Save, CheckCircle, Eye, EyeOff,
  CreditCard, ExternalLink, AlertTriangle, Loader, XCircle, Unlink
} from 'lucide-react';
import GoogleCalendarIntegration from './GoogleCalendarIntegration';
import ImportWizard from '../ImportWizard';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ── Stripe Connect Section ────────────────────────────────────────────────────
const StripeConnectSection = () => {
  const [status, setStatus]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [feedback, setFeedback]   = useState(null);

  // Lire le résultat du callback OAuth dans l'URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeResult = params.get('stripe');
    if (stripeResult === 'success') {
      setFeedback({ type: 'success', message: 'Stripe account connected successfully!' });
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    } else if (stripeResult === 'error') {
      const reason = params.get('reason') || 'unknown';
      setFeedback({ type: 'error', message: `Stripe connection failed: ${reason}` });
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/company/stripe/status`);
      setStatus(res.data);
    } catch (e) {
      console.error('Error fetching Stripe status:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    // Redirige vers le flux OAuth Stripe Connect
    window.location.href = `${API_BASE}/company/stripe/connect`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Stripe account? Online payments will be disabled.')) return;
    setDisconnecting(true);
    try {
      await axios.delete(`${API_BASE}/company/stripe/disconnect`);
      setFeedback({ type: 'success', message: 'Stripe account disconnected.' });
      await fetchStatus();
    } catch (e) {
      setFeedback({ type: 'error', message: 'Error disconnecting Stripe account.' });
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="border border-gray-700 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-indigo-400" />
          <div>
            <h4 className="text-white font-medium">Stripe Connect</h4>
            <p className="text-xs text-gray-500">Accept online payments from your clients</p>
          </div>
        </div>

        {/* Status badge */}
        {!loading && status && (
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
            status.stripe_onboarded
              ? 'bg-green-500/20 text-green-400 border-green-500/30'
              : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full bg-current ${status.stripe_onboarded ? 'animate-pulse' : ''}`} />
            {status.stripe_onboarded ? 'Connected' : 'Not connected'}
          </span>
        )}
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
          feedback.type === 'success'
            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
            : 'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {feedback.type === 'success'
            ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
            : <XCircle className="w-4 h-4 flex-shrink-0" />}
          {feedback.message}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
          <Loader className="w-4 h-4 animate-spin" />
          Loading Stripe status...
        </div>
      ) : status?.stripe_onboarded ? (
        // ── CONNECTÉ ──────────────────────────────────────────────────────────
        <div className="space-y-3">
          <div className="bg-gray-900/60 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Account</span>
              <span className="text-white font-mono">{status.stripe_account_preview}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">AeroTrick fee</span>
              <span className="text-indigo-400 font-semibold">{status.aerotrick_fee_percent}%</span>
            </div>
            {status.stripe_onboarded_at && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Connected on</span>
                <span className="text-gray-300">
                  {new Date(status.stripe_onboarded_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-900/40 rounded-lg p-3">
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
            A {status.aerotrick_fee_percent}% platform fee is automatically applied 
            on each transaction by AeroTrick.
          </div>

          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm transition disabled:opacity-50"
          >
            {disconnecting
              ? <Loader className="w-4 h-4 animate-spin" />
              : <Unlink className="w-4 h-4" />}
            {disconnecting ? 'Disconnecting...' : 'Disconnect Stripe'}
          </button>
        </div>
      ) : (
        // ── NON CONNECTÉ ──────────────────────────────────────────────────────
        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Connect your Stripe account to accept fuel payments, hangar fees, 
            and other charges directly from your clients through the AeroTrick 
            client portal.
          </p>

          <ul className="space-y-1.5 text-xs text-gray-500">
            {[
              'Payments go directly to your Stripe account',
              'Automatic invoicing and reconciliation',
              'Supports cards, Apple Pay, Google Pay',
              `AeroTrick platform fee: ${status?.aerotrick_fee_percent ?? 1.50}% per transaction`,
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          <button
            onClick={handleConnect}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 active:bg-indigo-700 rounded-lg text-white text-sm font-semibold transition"
          >
            <CreditCard className="w-4 h-4" />
            Connect with Stripe
            <ExternalLink className="w-3.5 h-3.5 opacity-70" />
          </button>

          <p className="text-xs text-gray-600">
            Secured by Stripe · 256-bit TLS encryption
          </p>
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const IntegrationsSettings = () => {
  const [saved, setSaved]           = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [settings, setSettings]     = useState({
    opensky_enabled: true,
    opensky_username: '',
    opensky_password: '',
    flightaware_enabled: false,
    flightaware_api_key: '',
    webhook_url: '',
    webhook_events: ['landing', 'takeoff', 'fuel_sale']
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      {/* DATA IMPORT WIZARD */}
      <ImportWizard />

      {/* GOOGLE CALENDAR */}
      <GoogleCalendarIntegration />

      {/* OTHER INTEGRATIONS */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Other Integrations</h3>
            <p className="text-gray-400 text-sm">Connect external services and APIs</p>
          </div>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
          >
            <Save className="w-4 h-4" />
            Save Integrations
          </button>
        </div>

        {saved && (
          <div className="mb-4 p-3 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30">
            <CheckCircle className="w-4 h-4 inline mr-2" />
            Integration settings saved successfully!
          </div>
        )}

        <div className="space-y-6">

          {/* ── STRIPE CONNECT — NEW ── */}
          <StripeConnectSection />

          {/* OpenSky */}
          <div className="border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Cloud className="w-5 h-5 text-blue-400" />
                <h4 className="text-white font-medium">OpenSky Network</h4>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.opensky_enabled}
                  onChange={(e) => setSettings({ ...settings, opensky_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
              </label>
            </div>
            {settings.opensky_enabled && (
              <div className="space-y-3 pl-8">
                <input
                  type="text"
                  placeholder="Username"
                  value={settings.opensky_username}
                  onChange={(e) => setSettings({ ...settings, opensky_username: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={settings.opensky_password}
                  onChange={(e) => setSettings({ ...settings, opensky_password: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                />
              </div>
            )}
          </div>

          {/* FlightAware */}
          <div className="border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-green-400" />
                <h4 className="text-white font-medium">FlightAware AeroAPI</h4>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.flightaware_enabled}
                  onChange={(e) => setSettings({ ...settings, flightaware_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
              </label>
            </div>
            {settings.flightaware_enabled && (
              <div className="space-y-3 pl-8">
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    placeholder="API Key"
                    value={settings.flightaware_api_key}
                    onChange={(e) => setSettings({ ...settings, flightaware_api_key: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm pr-10"
                  />
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Webhook */}
          <div className="border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <Webhook className="w-5 h-5 text-yellow-400" />
              <h4 className="text-white font-medium">Webhook Endpoint</h4>
            </div>
            <div className="space-y-3">
              <input
                type="url"
                placeholder="https://your-server.com/webhook"
                value={settings.webhook_url}
                onChange={(e) => setSettings({ ...settings, webhook_url: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
              />
              <div className="flex flex-wrap gap-3">
                {['landing', 'takeoff', 'fuel_sale', 'new_visitor', 'parking_start', 'parking_end'].map((event) => (
                  <label key={event} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.webhook_events.includes(event)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSettings({ ...settings, webhook_events: [...settings.webhook_events, event] });
                        } else {
                          setSettings({ ...settings, webhook_events: settings.webhook_events.filter(ev => ev !== event) });
                        }
                      }}
                      className="rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300 capitalize">{event.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default IntegrationsSettings;